import { CommonModule } from '@angular/common';
import { Component, inject, signal, computed, OnInit, ViewChild, effect, untracked, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ApiService } from '../../../core/services/api.service';
import { ProductConfigService } from '../../../core/services/product-config.service';
import { PaginatedTableComponent, TableHeader } from "../../../shared/components/tables/paginated-table/paginated-table.component";
import { DatePickerComponent, DateRangePickerOutput, DateRangePickerValue } from "../../../shared/components/date-picker/date-picker.component";
import { PaginationComponent } from "../../../shared/components/pagination/pagination.component";
import { DropdownComponent, DropdownOption } from "../../../shared/components/ui/dropdown/dropdown.component";
import { IconComponentV2 } from "../../../shared/components/icon-component/icon.component";
import { CallDetailsComponent } from './call-details/call-details.component';
import { IconName } from '../../../shared/icons/icon.component';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import dayjs from 'dayjs';
import { Router } from '@angular/router';
import { CommonService } from '../../../core/services/common.service';
import { BreakpointObserver } from '@angular/cdk/layout';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef } from '@angular/core';
import { environment } from '../../../../environments/environment';

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

const LA_TIMEZONE = 'America/Los_Angeles';

@Component({
  imports: [
    CommonModule, 
    FormsModule, 
    PaginatedTableComponent, 
    DatePickerComponent, 
    DropdownComponent, 
    IconComponentV2,
    MatDialogModule
  ],
  templateUrl: './call-history.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: ``
})
export class CallHistoryComponent implements OnInit, OnDestroy {

  _apiservice: ApiService = inject(ApiService);
  _matDialog: MatDialog = inject(MatDialog);
  _productConfigService = inject(ProductConfigService);
  _router: Router = inject(Router);
  _commonService = inject(CommonService);
  private _breakpointObserver = inject(BreakpointObserver);
  private _destroyRef = inject(DestroyRef);

  currentPage = 1;
  callHistory = signal<any[]>([]);
  pageSize = 10;
  totalItems = signal<number>(0);
  customers = signal<any[]>([]);
  agents = signal<any[]>([]);
  productsList = signal<DropdownOption[]>([]);
  aiTaskList = signal<any[]>([]);
  callTypeList = signal<any[]>([]);
  isLoading = signal<boolean>(false);
  isCustomersLoading = signal<boolean>(false);
  isAgentsLoading = signal<boolean>(false);

  customerPage = 1;
  customerLimit = 20;
  totalCustomers = 0;
  currentCustomerSearch = '';

  agentPage = 1;
  agentLimit = 20;
  totalAgents = 0;
  currentAgentSearch = '';

  selectedDateRange = signal<{ start: string; end: string } | null>({
    start: dayjs().tz(LA_TIMEZONE).subtract(29, 'day').startOf('day').toISOString(),
    end: dayjs().tz(LA_TIMEZONE).endOf('day').toISOString()
  });
  selectedProduct = signal<string | null>('');

  datePickerValue = signal<DateRangePickerValue>({
    startDate: dayjs().tz(LA_TIMEZONE).subtract(29, 'day').startOf('day').toISOString(),
    endDate: dayjs().tz(LA_TIMEZONE).endOf('day').toISOString(),
    unit: 'day' as const,
    comparisonType: 'none' as const
  });

  headers: TableHeader[] = [
    { label: 'Customer', key: 'customerName', subKey: 'customerEmail' },
    { label: 'Product', key: 'productName', cellType: 'badge' },
    { label: 'Agent', key: 'agentName', subKey: 'agentEmail' },
    { label: 'Team', key: 'teamName', subKey: 'teamCategory' },
    { label: 'Last Activity', key: 'lastDate', subKey: 'lastTime', dateFormat: 'mediumDate', subDateFormat: 'shortTime' }
  ];

  customerOptions = computed(() => {
    const data = this.customers();
    const customers = [...(Array.isArray(data) ? data : []).map(c => ({ value: c.id, label: c.name || c.email }))];
    if (this.appendAllCustomers) {
      customers.unshift({ value: '', label: 'All Customers' });
    }
    return customers;
  });

  agentOptions = computed(() => {
    const data = this.agents();
    const agents = [...(Array.isArray(data) ? data : []).map(a => ({ value: a.id, label: a.name || a.email }))];
    if (this.appendAllAgents) {
      agents.unshift({ value: '', label: 'All Agents' });
    }
    return agents;
  });

  selectedCustomer = signal<string | null>(null);
  selectedAgent = signal<string | null>(null);
  searchQuery = signal<string>('');

  productOptions = computed(() => {
    return [
      { value: '', label: 'All Products' },
      ...this.productsList().filter((p: any) => p.label !== 'Prism')
    ];
  });

  tableActionOptions = {
    showToggleButton: true,
    columnLabel: 'Action',
    useIconV2: true,
    toggleOptions: [
      {
        isButton: true,
        label: 'View Details',
        value: 'VIEW_DETAILS',
        iconName: 'eye',
        tooltip: 'View Details',
        isShow: true,
        color: '#A446FF'
      }
    ]
  };
  appendAllCustomers = true;
  appendAllAgents = true;
  isSmallScreen = signal<boolean>(false);

  private _isChangingSource = false;
  
  constructor() {
    this._breakpointObserver.observe(['(max-width: 1366px)']).subscribe((result) => {
      this.isSmallScreen.set(result.matches);
    });
  }

  ngOnInit(): void {
    this.fetchAllCustomers();
    this.fetchAllAgents();
    this.fetchAllProducts();
    this.fetchData();
  }

  fetchData() {
    const params: any = {
      page: this.currentPage,
      limit: this.pageSize,
      startDate: dayjs.utc(this.selectedDateRange()?.start).tz(LA_TIMEZONE).format('YYYY-MM-DD'),
      endDate: dayjs.utc(this.selectedDateRange()?.end).tz(LA_TIMEZONE).format('YYYY-MM-DD'),
    }

    if (this.selectedCustomer()) params.customerId = this.selectedCustomer();
    if (this.selectedAgent()) params.agentId = this.selectedAgent();
    if (this.selectedProduct()) params.productId = this.selectedProduct();
    if (this.searchQuery()) params.searchString = this.searchQuery();
    
    if (this.selectedDateRange()) {
      const start = this.selectedDateRange()?.start;
      const end = this.selectedDateRange()?.end;
      // params.start_date = dayjs.utc(start).tz(LA_TIMEZONE).format('YYYY-MM-DD');
      // params.end_date = dayjs.utc(end).tz(LA_TIMEZONE).format('YYYY-MM-DD');
    }
    
    this.isLoading.set(true);
    this._apiservice.get(`/call-history/list`, params, {showLoader: true}).subscribe({
      next: (res: any) => {
        if (res) {
          const mappedData = (res.groupedConversations || []).map((call: any) => {
            const product = call.productDetails?.[0];
            const config = this._productConfigService.getProductConfig(product?.name || '');
            
            return {
              ...call,
              customerName: call.customerDetails?.name || 'N.A',
              customerEmail: call.customerDetails?.email || 'N.A',
              agentName: call.agentDetails?.name || 'N.A',
              agentEmail: call.agentDetails?.email || 'N.A',
              productName: product?.name || 'N.A',
              productName_class: config ? `${config.chatbotTheme?.accentBg} ${config.chatbotTheme?.accentText}` : 'bg-slate-100 text-slate-700',
              teamName: call.teamDetails?.[0]?.name || 'N.A',
              teamCategory: call.teamDetails?.[0]?.category || '', // Guessing category
              lastDate: call.updatedAt || call.createdAt,
              lastTime: call.updatedAt || call.createdAt, 
              action: '' // Will use tableActionOptions instead
            };
          });

          this.callHistory.set(mappedData);
          this.totalItems.set(res.pagination.totalRecords);
        }
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error('Error fetching call history:', err);
        this.isLoading.set(false);
      }
    });
  }

  ngOnDestroy(): void {
    this._matDialog.closeAll();
  }

  fetchAllProducts() {
    this._apiservice.fetchProducts().subscribe({
      next: (response: any) => {
        const stored = sessionStorage.getItem('dynamic_routes');
        const dynamicRoutes = stored ? JSON.parse(stored) : [];
        const allowedProductIds = dynamicRoutes.map((item: any) => item.product?.id).filter(Boolean);

        const filteredProducts = response
          .filter((item: any) => item.active && allowedProductIds.includes(item.id))
          .map((item: any) => ({
            value: item.id,
            label: item.name,
          }));

        this.productsList.set(filteredProducts);
      }
    });
  }

  fetchAllCustomers(search: string = '', page: number = 1, append: boolean = false) {
    const params: any = {
      page: page,
      limit: this.customerLimit
    };
    if (search) params.search = search;

    this.isCustomersLoading.set(true);
    this._apiservice.get(`/customer-records/all`, params, { showErrorToaster: true }).subscribe((res: any) => {
      if (res) {
        const newRecords = res.customerRecords || (Array.isArray(res) ? res : []);
        if (append) {
          this.appendAllCustomers = true;
          this.customers.update(prev => [...prev, ...newRecords]);
        } else {
          this.appendAllCustomers = false;
          this.customers.set(newRecords);
        }
        this.totalCustomers = res.pagination?.total || (append ? this.totalCustomers : newRecords.length);
      }
      this.isCustomersLoading.set(false);
    });
  }

  onCustomerSearch(query: string) {
    this.currentCustomerSearch = query;
    this.customerPage = 1;
    this.fetchAllCustomers(query, 1, false);
  }

  onCustomerScrollEnd() {
    if (this.isCustomersLoading() || (this.totalCustomers > 0 && this.customers().length >= this.totalCustomers)) {
      return;
    }

    this.customerPage++;
    this.fetchAllCustomers(this.currentCustomerSearch, this.customerPage, true);
  }

  fetchAllAgents(search: string = '', page: number = 1, append: boolean = false) {
    const params: any = {
      page: page,
      limit: this.agentLimit
    };
    if (search) params.search = search;

    this.isAgentsLoading.set(true);
    this._apiservice.get(`/agent-records/all`, params, { showErrorToaster: true }).subscribe((res: any) => {
      if (res) {
        const newRecords = res.agentRecords || (Array.isArray(res) ? res : []);
        if (append) {
          this.appendAllAgents = true;
          this.agents.update(prev => [...prev, ...newRecords]);
        } else {
          this.appendAllAgents = false;
          this.agents.set(newRecords);
        }
        this.totalAgents = res.pagination?.total || (append ? this.totalAgents : newRecords.length);
      }
      this.isAgentsLoading.set(false);
    });
  }

  onAgentSearch(query: string) {
    this.currentAgentSearch = query;
    this.agentPage = 1;
    this.fetchAllAgents(query, 1, false);
  }

  onAgentScrollEnd() {
    if (this.isAgentsLoading() || (this.totalAgents > 0 && this.agents().length >= this.totalAgents)) {
      return;
    }

    this.agentPage++;
    this.fetchAllAgents(this.currentAgentSearch, this.agentPage, true);
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.fetchData();
  }

  onPageSizeChange(size: number) {
    this.pageSize = size;
    this.currentPage = 1;
    this.fetchData();
  }

  onDateRangeChange(value: DateRangePickerOutput) {
    this.selectedDateRange.set({
      start: value.start_date,
      end: value.end_date
    });
  }

  applyFilters() {
    this.currentPage = 1;
    this.fetchData();
  }

  resetFilters() {
    this.selectedCustomer.set('');
    this.selectedAgent.set('');
    this.selectedProduct.set('');
    this.searchQuery.set('');
    const defaultValue = {
      startDate: dayjs().tz(LA_TIMEZONE).subtract(29, 'day').startOf('day').toISOString(),
      endDate: dayjs().tz(LA_TIMEZONE).endOf('day').toISOString(),
      unit: 'day' as const,
      comparisonType: 'none' as const
    };
    this.selectedDateRange.set({
      start: defaultValue.startDate,
      end: defaultValue.endDate
    });
    this.datePickerValue.set(defaultValue);
    this.currentPage = 1;
    this.fetchData();
  }

  clearFilters() {
    this.resetFilters();
  }
  
  private _dialogRef: any = null;

  @ViewChild('audioRecording') audioRecording!: any;
  activePlayingCall = signal<any>(null);
  isPlaying = signal<boolean>(false);
  currentTime = signal<number>(0);
  audioDuration = signal<number>(0);

  onActionSelected(event: { action: string; row: any }) {
    if (event.action === 'VIEW_DETAILS') {
      this.openDetails(event.row);
    } else if (event.action === 'PLAY') {
      this.openAudioPlayer(event.row);
    }
  }

  openAudioPlayer(call: any) {
    this.prepareRecordingData(call);
    this.setupAudioListeners();
    
    if (!this._dialogRef) {
      this._dialogRef = this._matDialog.open(this.audioRecording, {
        width: '420px',
        hasBackdrop: false,
        position: { bottom: '40px', right: '40px' },
        panelClass: 'audio-player-dialog-panel'
      });

      this._dialogRef.afterClosed().subscribe(() => {
        this._dialogRef = null;
        this.closePlayer();
      });
    }
  }

  closePlayer(): void {
    this.audio.pause();
    if (this._dialogRef) {
      this._dialogRef.close();
      this._dialogRef = null;
    }
    this.activePlayingCall.set(null);
    this.isPlaying.set(false);
  }

  private audio = new Audio();
  private setupAudioListeners(): void {
    this.audio.ontimeupdate = (e: any) => {
      const baseOffset = this.activePlayingCall()?.baseOffset || 0;
      this.currentTime.set(this.audio.currentTime + baseOffset);
    };
    this.audio.onloadedmetadata = (e: any) => {
      // For streaming, the browser duration might just be the chunk length
      // We rely on the duration we fetched or estimated
    };
    this.audio.onended = () => {
      this.isPlaying.set(false);
      this.currentTime.set(this.audioDuration());
    };
    this.audio.onplay = () => this.isPlaying.set(true);
    this.audio.onpause = () => this.isPlaying.set(false);
  }

  seek(event: MouseEvent): void {
    const el = event.currentTarget as HTMLElement;
    const rect = el.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const targetTime = percentage * this.audioDuration();
    
    if (isFinite(targetTime) && this.activePlayingCall()) {
      this.playStreamingAudio(this.activePlayingCall(), targetTime);
    }
  }

  private playStreamingAudio(recording: any, startTime: number = 0): void {
    const signedUrl = recording.audioUrl || recording.audioUrls?.[0]?.signedUrl;
    if (!signedUrl) return;

    const flooredStart = Math.floor(startTime);
    const streamUrl = `${environment.apiUrl}/call-audit/stream-audio?url=${encodeURIComponent(signedUrl)}&productName=${encodeURIComponent(recording.productName || '')}&referer=${encodeURIComponent(recording.meetLink || '')}&startTime=${flooredStart}`;

    const baseOffset = Math.floor(flooredStart);
    this.activePlayingCall.update(c => c ? ({ ...c, baseOffset }) : ({ ...recording, baseOffset }));
    this.currentTime.set(startTime);

    this.audio.pause();
    this.audio.src = streamUrl;
    this.audio.load();
    this.audio.oncanplay = () => {
      this.audio.oncanplay = null;
      this.audio.play().catch(err => console.error('Audio playback error:', err));
    };
  }

  prepareRecordingData(recording: any) {
    this.activePlayingCall.set({
      ...recording,
      audioUrl: recording.audioUrls?.[0]?.signedUrl || ''
    });
  }

  formatTime(time: number): string {
    if (!isFinite(time)) return '0:00';
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  onAudioMetadataLoaded(event: any) {
    this.audioDuration.set(event.target.duration);
  }

  onAudioTimeUpdate(event: any) {
    this.currentTime.set(event.target.currentTime);
  }

  togglePlay() {
    if (this.isPlaying()) {
      this.audio.pause();
    } else {
      // If audio has ended or is at the very end, restart from 0
      const duration = this.audioDuration();
      const current = this.currentTime();
      if (this.audio.ended || (duration > 0 && current >= duration - 0.1)) {
        const call = this.activePlayingCall();
        if (call) {
          this.playStreamingAudio(call, 0);
          return;
        }
      }

      if (!this.audio.src && this.activePlayingCall()) {
        this.playStreamingAudio(this.activePlayingCall(), this.currentTime());
      } else {
        this.audio.play().catch(err => console.error('Error resuming audio:', err));
      }
    }
  }

  openDetails(call: any) {
    this._router.navigate(['app/prism/call-history/details'], { state: { callData: call } });
  }
}
